/* eslint-disable @typescript-eslint/no-explicit-any */
import type React from "react";
import { useState, useCallback, useEffect, useRef } from "react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Label } from "@capacit-com/lara-law-shared-ui";
import Cropper from "react-easy-crop";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@capacit-com/lara-law-shared-ui";

interface ProfilePictureUploadProps {
  initialValue: string;
  onChange: (value: string) => void;
}

export function ProfilePictureUpload({
  initialValue,
  onChange,
}: ProfilePictureUploadProps) {
  const [preview, setPreview] = useState(initialValue);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          setPreview(base64String);
          setIsCropperOpen(true);
        };
        reader.readAsDataURL(file);
      }
    },
    []
  );

  const onCropComplete = useCallback(
    (_croppedArea: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: { width: number; height: number; x: number; y: number }
  ): Promise<string> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return imageSrc;
    }

    // Set canvas size to 256x256 (desired output size)
    canvas.width = 256;
    canvas.height = 256;

    // Draw the cropped image to fit within 256x256
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      256,
      256
    );

    return canvas.toDataURL("image/jpeg");
  };

  const handleCropSave = useCallback(async () => {
    if (croppedAreaPixels) {
      const croppedImage = await getCroppedImg(preview, croppedAreaPixels);
      setPreview(croppedImage);
      onChange(croppedImage);
      setIsCropperOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [croppedAreaPixels, preview, onChange]);

  useEffect(() => {
    setPreview(initialValue);
  }, [initialValue]);

  return (
    <div className="flex flex-col items-center space-y-4">
      <Avatar className="w-24 h-24">
        <AvatarImage src={preview} alt="Profile picture" />
        <AvatarFallback>Upload</AvatarFallback>
      </Avatar>
      <div>
        <input
          ref={fileInputRef}
          type="file"
          id="profilePicture"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
        <Label htmlFor="profilePicture" className="cursor-pointer">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            {preview ? "Skift billede" : "Upload billede"}
          </Button>
        </Label>
      </div>

      <Dialog open={isCropperOpen} onOpenChange={setIsCropperOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Beskær profilbillede</DialogTitle>
          </DialogHeader>
          <div className="relative w-full h-[300px]">
            <Cropper
              image={preview}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsCropperOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCropSave}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
